import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { get, every, invoke } from 'lodash';
import { useApp } from '../../context/app-context';
import { css } from '@emotion/css';
import Modal from '../../common/Modal/Modal';
import moment from 'moment/moment';
import { OrderContext, OrderContextType } from '../Order/OrderTile';
import { ItemsContext, ItemsContextType } from './ItemsContext';

const skipOrderStyles = css`
  color: #7b1a27;
  cursor: pointer;
  border: 0;
  background: 0;
`;

const buttonContainer = css`
  display: flex;
  justify-content: center;
`;

const confirmButtonStyle = css`
  background-color: #7b1a27;
  color: #fff;
  border: 0;
  border-radius: 6.25rem;
  padding: 1rem 0.75rem;
  min-width: 14rem;
  cursor: pointer;
`;

const SkipItemOnceLink = (props) => {
  const { className, text = 'Skip once', item } = props;
  const [loading, setLoading] = useState(false);
  const { showToast, setDisplaySlotChangesMessage } = useApp();
  const [modal, setModal] = useState(false);
  const currentOrderContext = useContext(OrderContext) as OrderContextType;
  const snsNextOrderId = get(currentOrderContext, 'snsNextOrderId');
  const orderDate = get(currentOrderContext, 'orderDate');
  const itemProps = ['nextOrderId', 'nextOrderItemId', 'subscriptionId', 'orderDate'];
  const nextOrderDate = moment(get(item, 'futureOrderDate'), 'YYYY-M-D');
  const desc = `${get(item, 'itemDescription')} will be skipped for this order but included in next future order on ${nextOrderDate.format('ddd MMM DD')}.`;
  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;

  const itemInfo = {
    nextOrderId: snsNextOrderId,
    nextOrderItemId: get(item, 'nextOrderItemId'),
    subscriptionId: get(item, 'subscriptionId'),
    orderDate: orderDate,
    isReplinishItem: get(item, 'isReplinishItem'),
    itemBpn: get(item, 'productBpn'),
  };

  const showModal = () => {
    // only show modal if we have all the info, confirm this later
    if (every(itemProps, (prop) => get(itemInfo, prop))) {
      setModal(true);
      analyticsEvents('ar:skip-once|view', 'modalView');
    }
  };

  const attemptSkipItem = async () => {
    setLoading(true);

    try {
      await invoke(currentItemsContext, 'skipItemOnce', itemInfo);

      setDisplaySlotChangesMessage(true);
      showToast(
        'info',
        `${get(item, 'itemDescription')} skipped for ${moment(orderDate, 'YYYY-M-D').format('MMM D')} order.`,
      );

      //refreshing the tab info only on success
      await invoke(currentItemsContext, 'fetchItemsInfo');
      analyticsEvents('ar:skip-once|success', 'modalClick');
    } catch (err) {
      showToast('error', `We couldn't complete your skip item request. Please try again later.`);
      analyticsEvents('ar:skip-once|un-success', 'modalClick');
    } finally {
      setModal(false);
    }

    setLoading(false);
  };

  const analyticsEvents = (name, eventType) => {
    try {
      if (window['AB']?.DATALAYER) {
        window['AB'].DATALAYER.handleModalAndLinkEvents({
          eventType: eventType,
          modalAndLinkData: name,
        });
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  return (
    <div className={className}>
      <button
        className={`${skipOrderStyles} link-sm`}
        onClick={showModal}
        aria-label="skip item once"
        disabled={loading}
      >
        <span>{text}</span>
      </button>

      <Modal titleText="Skip once" description={desc} onClose={() => setModal(false)} show={modal} maxWidth={28}>
        <div className={buttonContainer}>
          <button
            className={`${confirmButtonStyle} body-text`}
            onClick={attemptSkipItem}
            disabled={loading}
            aria-label="Confirm order skip"
          >
            {loading ? '...' : 'Confirm'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

SkipItemOnceLink.propTypes = {
  text: PropTypes.string,
  item: PropTypes.object.isRequired,
};

export default SkipItemOnceLink;
